/**
 * Whether the viewport is the phone one, as a subscription rather than a one-shot read.
 *
 * ## 🔴 Why a hook, when a media query is right there
 *
 * Because **hiding a table column in CSS does not remove it**. Three attempts proved it: a
 * `<col>` keeps its width when every cell in its column is `display: none` (the phone table
 * measured 644px in a 430px viewport, which was exactly the tracks nobody could see); zeroing
 * those tracks by class ties with the width rule and loses to whichever is written later; and
 * zeroing them by specificity fixed the *width* but left the visible cells drawing on top of one
 * another, because a fixed layout still allocates the collapsed tracks.
 *
 * A table's column set is structure, and CSS is the wrong tool for changing structure. The right
 * fix is to render the columns the viewport can hold, which needs the breakpoint in JavaScript.
 *
 * ⚠ **`720px` must match `app.css`'s phone block.** There is no way to share one constant between
 * a stylesheet and a module without a build step, so the pair is kept honest by both citing each
 * other. If one moves, the drawer and the table disagree about what a phone is.
 */

import { useEffect, useState } from 'react';

/** Matches the `@media (max-width: 720px)` block in `app.css`. Keep the two in step. */
export const PHONE_QUERY = '(max-width: 720px)';

export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = (event: MediaQueryListEvent): void => setIsPhone(event.matches);
    // Read once on mount as well: a resize that crossed the breakpoint between the initial state
    // and this effect would otherwise leave the table rendering the wrong column set.
    setIsPhone(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isPhone;
}
